import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { X, UploadCloud } from 'lucide-react';
import { parseGeoFile, extractLargestExteriorRing, sanitizeCoordinates, ACCEPT_FILE_EXTENSIONS } from '../utils/geoFileParser';
export const UploadBoundaryModal = ({ isOpen, onClose, projectName, projectDescription, onSuccess, onStartDrawing }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [previewCoords, setPreviewCoords] = useState(null);
    const inputRef = useRef(null);
    const onBrowseClick = useCallback(() => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, []);
    const handleFiles = useCallback(async (files) => {
        if (!files || files.length === 0)
            return;
        const file = files[0];
        setError(null);
        setSelectedFile(file);
        setIsParsing(true);
        try {
            const { geojson, error: parseError } = await parseGeoFile(file);
            if (parseError || !geojson) {
                setError(parseError || 'Failed to parse file.');
                setPreviewCoords(null);
                return;
            }
            let coords = extractLargestExteriorRing(geojson);
            if (!coords || coords.length < 3) {
                setError('No valid polygon found in the file.');
                setPreviewCoords(null);
                return;
            }
            coords = sanitizeCoordinates(coords);
            if (coords.length < 3) {
                setError('Polygon ring after normalization has fewer than 3 unique vertices.');
                setPreviewCoords(null);
                return;
            }
            setPreviewCoords(coords);
        }
        catch (e) {
            console.error(e);
            setError((e === null || e === void 0 ? void 0 : e.message) || 'Failed to parse file.');
            setPreviewCoords(null);
        }
        finally {
            setIsParsing(false);
        }
    }, []);
    const onDrop = useCallback((e) => {
        var _a;
        e.preventDefault();
        e.stopPropagation();
        if ((_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.files) {
            void handleFiles(e.dataTransfer.files);
        }
    }, [handleFiles]);
    const onDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);
    const viewBoxAndPath = useMemo(() => {
        if (!previewCoords || previewCoords.length < 3) {
            return { viewBox: '0 0 100 100', path: '' };
        }
        const lats = previewCoords.map(c => c.lat);
        const lngs = previewCoords.map(c => c.lng);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const latRange = maxLat - minLat || 1;
        const lngRange = maxLng - minLng || 1;
        const padding = Math.max(latRange, lngRange) * 0.1;
        const paddedMinLat = minLat - padding;
        const paddedMaxLat = maxLat + padding;
        const paddedMinLng = minLng - padding;
        const paddedMaxLng = maxLng + padding;
        const aspectRatio = (paddedMaxLng - paddedMinLng) / (paddedMaxLat - paddedMinLat || 1);
        let width = 300;
        let height = 300;
        if (aspectRatio > 1) {
            height = width / aspectRatio;
        }
        else {
            width = height * aspectRatio;
        }
        const svgPoints = previewCoords.map(coord => {
            const x = ((coord.lng - paddedMinLng) / (paddedMaxLng - paddedMinLng)) * width;
            const y = height - ((coord.lat - paddedMinLat) / (paddedMaxLat - paddedMinLat)) * height;
            return `${x},${y}`;
        });
        const path = `M ${svgPoints.join(' L ')} Z`;
        return { viewBox: `0 0 ${width} ${height}`, path };
    }, [previewCoords]);
    const onConfirm = useCallback(() => {
        if (!previewCoords || previewCoords.length < 3) {
            setError('Please select a valid boundary file containing a polygon.');
            return;
        }
        // Hand off to draw flow: inject coordinates for pre-population
        window.__initialUploadCoordinates = previewCoords;
        // Begin drawing mode immediately, identical flow to manual draw
        if (onStartDrawing) {
            onStartDrawing(projectName, projectDescription);
        }
        if (onSuccess)
            onSuccess();
        onClose();
    }, [previewCoords, projectName, projectDescription, onStartDrawing, onClose]);
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-[70] flex items-center justify-center p-4", children: [_jsx("div", { className: "ls-modal-backdrop", onClick: onClose }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative w-full max-w-2xl flex flex-col", style: { maxHeight: '92vh' }, children: [_jsxs("div", { className: "flex items-center justify-between p-6 ls-modal-band-top shrink-0", style: { backgroundColor: 'var(--forest)' }, children: [_jsx("h2", { className: "text-xl font-semibold", style: { color: 'var(--sand)' }, children: "Upload Boundary File" }), _jsx("button", { onClick: onClose, className: "p-1 rounded hover:bg-opacity-20 hover:bg-white transition-colors", style: { color: 'var(--sand)' }, children: _jsx(X, { className: "w-5 h-5 sm:w-6 sm:h-6" }) })] }), _jsxs("div", { className: "p-6 space-y-4 overflow-y-auto min-h-0 flex-1", style: { backgroundColor: 'var(--sand)', color: 'var(--charcoal)' }, children: [_jsx("p", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: "Upload a boundary as GeoJSON (.geojson), KML (.kml), or a zipped Shapefile (.zip)." }), _jsxs("div", { onDrop: onDrop, onDragOver: onDragOver, className: "flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-lg p-8 cursor-pointer", style: { borderColor: 'var(--pewter)', backgroundColor: 'white' }, onClick: onBrowseClick, children: [_jsx(UploadCloud, { className: "w-8 h-8", style: { color: 'var(--forest)' } }), _jsx("div", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: "Drag and drop your file here, or click to browse" }), _jsx("div", { className: "text-xs", style: { color: 'var(--pewter)' }, children: "Accepted: .geojson, .kml, .zip" }), _jsx("input", { ref: inputRef, type: "file", accept: ACCEPT_FILE_EXTENSIONS.join(','), className: "hidden", onChange: (e) => handleFiles(e.target.files) })] }), selectedFile && (_jsxs("div", { className: "text-sm", style: { color: 'var(--charcoal)' }, children: ["Selected: ", _jsx("span", { className: "font-medium", children: selectedFile.name }), isParsing && _jsx("span", { className: "ml-2 opacity-75", children: "Parsing\u2026" })] })), error && (_jsx("div", { className: "text-sm p-3 rounded-lg", style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }, children: error })), _jsx("div", { className: "w-full h-64 rounded-lg border-2 flex items-center justify-center", style: { backgroundColor: 'white', borderColor: 'var(--pewter)' }, children: previewCoords && previewCoords.length >= 3 ? (_jsx("svg", { viewBox: viewBoxAndPath.viewBox, className: "w-full h-full p-4", style: { maxWidth: '100%', maxHeight: '100%' }, children: _jsx("path", { d: viewBoxAndPath.path, fill: "rgba(93, 78, 47, 0.3)", stroke: "var(--forest)", strokeWidth: "2" }) })) : (_jsx("span", { className: "text-sm", style: { color: 'var(--pewter)' }, children: "No preview available" })) }), _jsxs("div", { className: "flex flex-col sm:flex-row justify-center gap-3 sm:gap-4", children: [_jsx("button", { onClick: onClose, className: "flex items-center justify-center gap-2 px-6 py-3 rounded-lg border-2 font-medium transition-colors hover:bg-opacity-10", style: { borderColor: 'var(--pewter)', color: 'var(--pewter)', backgroundColor: 'transparent' }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = 'var(--pewter)'; e.currentTarget.style.color = 'var(--sand)'; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--pewter)'; }, children: "Cancel" }), _jsx("button", { onClick: onConfirm, disabled: isParsing || !previewCoords, className: "flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Continue" })] })] })] })] }));
};
export default UploadBoundaryModal;
